import { Dispatch, SetStateAction } from 'react'

import useSWR from 'swr'

import { MutationOptions, useCallMutation } from '@/common/api/client'
import { apiDefaultFetcher, apiPostFetcher } from '@/common/api/fetcher'
import { DEFAULT_PAGE, DEFAULT_PER_PAGE } from '@/common/api/pagination'
import {
  BehaviorAnalysisResponse,
  BehaviorAnalysisVars,
  CustomerApiPath,
  GenerateCustomerInsightResponse,
  GenerateCustomerInsightVars,
  ListCustomersResponse,
  ListCustomersVars,
  RunVisitorInsightVars,
  VisitorInsightListResponse,
  VisitorInsightListVars,
  VisitorInsightRunResponse,
  VisitorSummaryResponse,
  VisitorSummaryVars
} from '@/common/api/params/customer'
import { SearchConditionBase, SearchConditionParam, useSearchCondition } from '@/common/api/search'

export const useCustomerListApi = (vars: ListCustomersVars) => {
  return useSWR<ListCustomersResponse, Response, [string, ListCustomersVars]>([
    CustomerApiPath.LIST_CUSTOMERS,
    vars
  ])
}

export const useCustomerBehaviorAnalyticsGetApi = (vars: BehaviorAnalysisVars) => {
  return useSWR<BehaviorAnalysisResponse, Response, [string, BehaviorAnalysisVars]>([
    CustomerApiPath.BEHAVIOR_ANALYSIS,
    vars
  ])
}

export const useCustomerInsightGetApi = (vars: GenerateCustomerInsightVars) => {
  return useSWR<GenerateCustomerInsightResponse, Response, [string, GenerateCustomerInsightVars]>(
    [CustomerApiPath.GENERATE_CUSTOMER_INSIGHT, vars],
    apiPostFetcher
  )
}

// 2026-10-04 追加（SMART_HUB_DEV-127）：訪問者サマリ（AIなしの集計。即時に返る）
export const useVisitorSummaryApi = (vars: VisitorSummaryVars) => {
  return useSWR<VisitorSummaryResponse, Response, [string, VisitorSummaryVars]>([
    CustomerApiPath.VISITOR_SUMMARY,
    vars
  ])
}

/** 保存済みの訪問者サマリのAI示唆（新しい順・最大10世代） */
export const useVisitorSummaryInsightsApi = (vars: VisitorInsightListVars) => {
  return useSWR<VisitorInsightListResponse, Response, [string, VisitorInsightListVars]>([
    CustomerApiPath.VISITOR_SUMMARY_INSIGHTS,
    vars
  ])
}

/**
 * 訪問者サマリのAI示唆を生成して1世代として保存する。
 * AIを待つため**数十秒かかる**。呼び出し側で必ず進捗を出し、実行中はボタンを無効にすること。
 */
export const useRunVisitorSummaryInsightApi = (
  options?: MutationOptions<VisitorInsightRunResponse>
) => {
  return useCallMutation<VisitorInsightRunResponse, RunVisitorInsightVars>(
    CustomerApiPath.VISITOR_SUMMARY_INSIGHTS,
    options
  )
}

export const CustomerOrderBy = {
  VISITOR_ID_ASC: 'visitor-id-asc',
  VISITOR_ID_DESC: 'visitor-id-desc',
  CUSTOMER_ID_ASC: 'customer-id-asc',
  CUSTOMER_ID_DESC: 'customer-id-desc',
  DELIVERY_COUNT_ASC: 'delivery-count-asc',
  DELIVERY_COUNT_DESC: 'delivery-count-desc',
  VIEW_COUNT_ASC: 'view-count-asc',
  VIEW_COUNT_DESC: 'view-count-desc',
  VIEW_PRODUCT_COUNT_ASC: 'view-product-count-asc',
  VIEW_PRODUCT_COUNT_DESC: 'view-product-count-desc',
  CART_COUNT_ASC: 'cart-count-asc',
  CART_COUNT_DESC: 'cart-count-desc',
  ORDER_COUNT_ASC: 'order-count-asc',
  ORDER_COUNT_DESC: 'order-count-desc',
  FIRST_SEEN_ASC: 'first-seen-asc',
  FIRST_SEEN_DESC: 'first-seen-desc',
  LAST_SEEN_ASC: 'last-seen-asc',
  LAST_SEEN_DESC: 'last-seen-desc'
} as const

export type CustomerOrderBy = (typeof CustomerOrderBy)[keyof typeof CustomerOrderBy]

export const CustomerFilterStatus = {
  ALL: '',
  MEMBER: 'member',
  UNKNOWN: 'unknown'
} as const

export type CustomerFilterStatus = (typeof CustomerFilterStatus)[keyof typeof CustomerFilterStatus]

export interface CustomerSearchCondition extends SearchConditionBase {
  keyword: string | null
  status: CustomerFilterStatus | null
  orderBy: CustomerOrderBy | null
}

const customerSearchConditionParam: SearchConditionParam[] = [
  { name: 'keyword', type: 'string' },
  { name: 'status', type: 'string' },
  { name: 'orderBy', type: 'string' }
]

export const CUSTOMER_SEARCH_CONDITION_CLEAR: CustomerSearchCondition = {
  page: DEFAULT_PAGE,
  perPage: DEFAULT_PER_PAGE,
  keyword: null,
  status: null,
  orderBy: null
}

export const useAccessCustomerSearchCondition = (): [
  CustomerSearchCondition,
  Dispatch<SetStateAction<CustomerSearchCondition>>,
  (page: number) => void
] => {
  return useSearchCondition<CustomerSearchCondition>(customerSearchConditionParam)
}

export const convertConditionToCustomerListVars = (
  siteId: number,
  condition: CustomerSearchCondition
): ListCustomersVars => {
  return {
    site_id: siteId,
    page: condition.page,
    per_page: condition.perPage,
    ...(condition.keyword && { search: condition.keyword }),
    ...(condition.status && { status: condition.status }),
    ...(condition.orderBy
      ? {
          ...(condition.orderBy == CustomerOrderBy.VISITOR_ID_ASC && {
            sort: 'visitor_id',
            order: 'asc'
          }),
          ...(condition.orderBy == CustomerOrderBy.VISITOR_ID_DESC && {
            sort: 'visitor_id',
            order: 'desc'
          }),
          ...(condition.orderBy == CustomerOrderBy.CUSTOMER_ID_ASC && {
            sort: 'customer_id',
            order: 'asc'
          }),
          ...(condition.orderBy == CustomerOrderBy.CUSTOMER_ID_DESC && {
            sort: 'customer_id',
            order: 'desc'
          }),
          ...(condition.orderBy == CustomerOrderBy.DELIVERY_COUNT_ASC && {
            sort: 'delivery_count',
            order: 'asc'
          }),
          ...(condition.orderBy == CustomerOrderBy.DELIVERY_COUNT_DESC && {
            sort: 'delivery_count',
            order: 'desc'
          }),
          ...(condition.orderBy == CustomerOrderBy.VIEW_COUNT_ASC && {
            sort: 'view_count',
            order: 'asc'
          }),
          ...(condition.orderBy == CustomerOrderBy.VIEW_COUNT_DESC && {
            sort: 'view_count',
            order: 'desc'
          }),
          ...(condition.orderBy == CustomerOrderBy.VIEW_PRODUCT_COUNT_ASC && {
            sort: 'view_product_count',
            order: 'asc'
          }),
          ...(condition.orderBy == CustomerOrderBy.VIEW_PRODUCT_COUNT_DESC && {
            sort: 'view_product_count',
            order: 'desc'
          }),
          ...(condition.orderBy == CustomerOrderBy.CART_COUNT_ASC && {
            sort: 'cart_count',
            order: 'asc'
          }),
          ...(condition.orderBy == CustomerOrderBy.CART_COUNT_DESC && {
            sort: 'cart_count',
            order: 'desc'
          }),
          ...(condition.orderBy == CustomerOrderBy.ORDER_COUNT_ASC && {
            sort: 'order_count',
            order: 'asc'
          }),
          ...(condition.orderBy == CustomerOrderBy.ORDER_COUNT_DESC && {
            sort: 'order_count',
            order: 'desc'
          }),
          ...(condition.orderBy == CustomerOrderBy.FIRST_SEEN_ASC && {
            sort: 'first_seen',
            order: 'asc'
          }),
          ...(condition.orderBy == CustomerOrderBy.FIRST_SEEN_DESC && {
            sort: 'first_seen',
            order: 'desc'
          }),
          ...(condition.orderBy == CustomerOrderBy.LAST_SEEN_ASC && {
            sort: 'last_seen',
            order: 'asc'
          }),
          ...(condition.orderBy == CustomerOrderBy.LAST_SEEN_DESC && {
            sort: 'last_seen',
            order: 'desc'
          })
        }
      : {
          sort: 'last_seen',
          order: 'desc'
        })
  }
}
